import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../hooks';
import { logout, selectUser } from '../store/slices/authSlice';
import { ThemeToggle } from '../components/ThemeToggle';
import LoadingSpinner from '../components/LoadingSpinner';
import apiClient from '../services/api';
import { PlatformAnalytics, UsageAnalytics, SubscriptionAnalytics } from '../types/api';
import './Analytics.css';

const Analytics: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const user = useAppSelector(selectUser);

  const [isLoading, setIsLoading] = useState(true);
  const [platformAnalytics, setPlatformAnalytics] = useState<PlatformAnalytics | null>(null);
  const [usageAnalytics, setUsageAnalytics] = useState<UsageAnalytics | null>(null);
  const [subscriptionAnalytics, setSubscriptionAnalytics] = useState<SubscriptionAnalytics | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const checkAdminAndLoadData = async () => {
      try {
        const adminStatus = await apiClient.verifyAdminAccess();
        if (!adminStatus) {
          navigate('/dashboard');
          return;
        }
        setIsAdmin(true);
        await loadAnalytics();
      } catch (error) {
        console.error('Error checking admin access:', error);
        navigate('/dashboard');
      }
    };

    checkAdminAndLoadData();
  }, [navigate]);

  const loadAnalytics = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const [platformData, usageData, subscriptionData] = await Promise.all([
        apiClient.getPlatformAnalytics(),
        apiClient.getUsageAnalytics(),
        apiClient.getSubscriptionAnalytics()
      ]);

      setPlatformAnalytics(platformData);
      setUsageAnalytics(usageData);
      setSubscriptionAnalytics(subscriptionData);
    } catch (error: any) {
      console.error('Error loading analytics:', error);
      setError('Failed to load analytics data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/');
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  const getSubscriptionColor = (tier: string) => {
    switch (tier) {
      case 'premium': return '#7c3aed';
      case 'pro': return '#2563eb';
      case 'free': return '#6b7280';
      default: return '#6b7280';
    }
  };

  return (
    <div className="analytics">
      <header className="admin-header">
        <div className="header-content">
          <div>
            <h1>Platform Analytics</h1>
            <p>Monitor platform performance and user metrics</p>
          </div>
          <div className="header-actions">
            <ThemeToggle />
            <Link to="/admin" className="btn btn-outline">
              Back to Admin
            </Link>
            <button className="btn btn-secondary" onClick={handleLogout}>
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="analytics-main">
        {error && (
          <div className="alert alert-error">
            {error}
            <button
              className="alert-retry"
              onClick={loadAnalytics}
            >
              Retry
            </button>
          </div>
        )}

        {isLoading ? (
          <div className="loading-container">
            <LoadingSpinner size="lg" text="Loading analytics..." />
          </div>
        ) : (
          <div className="analytics-grid">
            {/* Overview Cards */}
            {platformAnalytics && (
              <section className="overview-cards">
                <h2>Platform Overview</h2>
                <div className="cards-grid">
                  <div className="analytics-card">
                    <div className="card-header">
                      <h3>Total Users</h3>
                      <span className="card-icon">👥</span>
                    </div>
                    <div className="card-value">{platformAnalytics.users.total.toLocaleString()}</div>
                    <div className="card-subtext">
                      {platformAnalytics.users.active} active, {platformAnalytics.users.inactive} inactive
                    </div>
                  </div>

                  <div className="analytics-card">
                    <div className="card-header">
                      <h3>Monthly Revenue</h3>
                      <span className="card-icon">💰</span>
                    </div>
                    <div className="card-value">
                      {formatCurrency(subscriptionAnalytics?.revenue?.estimated_monthly || 0)}
                    </div>
                    <div className="card-subtext">
                      {subscriptionAnalytics?.metrics?.paid_users || 0} paid subscribers
                    </div>
                  </div>

                  <div className="analytics-card">
                    <div className="card-header">
                      <h3>Content Created</h3>
                      <span className="card-icon">📚</span>
                    </div>
                    <div className="card-value">{platformAnalytics.content.total_memoirs.toLocaleString()}</div>
                    <div className="card-subtext">
                      {platformAnalytics.content.total_chapters.toLocaleString()} chapters, {platformAnalytics.content.total_words.toLocaleString()} words
                    </div>
                  </div>

                  <div className="analytics-card">
                    <div className="card-header">
                      <h3>Conversion Rate</h3>
                      <span className="card-icon">📈</span>
                    </div>
                    <div className="card-value">
                      {formatPercentage(subscriptionAnalytics?.metrics?.conversion_rate || 0)}
                    </div>
                    <div className="card-subtext">
                      Free to paid conversion
                    </div>
                  </div>
                </div>
              </section>
            )}

            {/* Subscription Analytics */}
            {platformAnalytics && (
              <section className="subscription-analytics">
                <h2>Subscription Distribution</h2>
                <div className="subscription-chart">
                  {Object.entries(platformAnalytics.subscriptions).map(([tier, count]) => (
                    <div key={tier} className="subscription-bar">
                      <div className="bar-label">
                        <span className="tier-name" style={{ color: getSubscriptionColor(tier) }}>
                          {tier.charAt(0).toUpperCase() + tier.slice(1)}
                        </span>
                        <span className="tier-count">{count}</span>
                      </div>
                      <div className="bar-container">
                        <div
                          className="bar-fill"
                          style={{
                            width: `${(count / Math.max(...Object.values(platformAnalytics.subscriptions)) * 100)}%`,
                            backgroundColor: getSubscriptionColor(tier)
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Usage Analytics */}
            {usageAnalytics && (
              <section className="usage-analytics">
                <h2>Usage Metrics</h2>
                <div className="usage-grid">
                  <div className="usage-card">
                    <h3>Users Near Limits</h3>
                    <div className="usage-stats">
                      <div className="usage-stat">
                        <span className="stat-label">Chapter Limit:</span>
                        <span className="stat-value">{usageAnalytics.users_approaching_chapter_limit}</span>
                      </div>
                      <div className="usage-stat">
                        <span className="stat-label">AI Limit:</span>
                        <span className="stat-value">{usageAnalytics.users_approaching_ai_limit}</span>
                      </div>
                    </div>
                  </div>

                  <div className="usage-card">
                    <h3>Content Metrics</h3>
                    <div className="usage-stats">
                      <div className="usage-stat">
                        <span className="stat-label">Avg Chapters/User:</span>
                        <span className="stat-value">{usageAnalytics.average_chapters_per_user.toFixed(1)}</span>
                      </div>
                      <div className="usage-stat">
                        <span className="stat-label">AI Requests (Month):</span>
                        <span className="stat-value">{usageAnalytics.total_ai_requests_this_month.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            )}

            {/* Revenue Insights */}
            {subscriptionAnalytics && (
              <section className="revenue-insights">
                <h2>Revenue Insights</h2>
                <div className="insights-grid">
                  <div className="insight-card">
                    <h3>Subscription Health</h3>
                    <div className="insight-content">
                      <div className="insight-metric">
                        <span className="metric-value">{subscriptionAnalytics.metrics?.expiring_soon || 0}</span>
                        <span className="metric-label">Expiring Soon</span>
                      </div>
                      <div className="insight-metric">
                        <span className="metric-value">{subscriptionAnalytics.metrics?.paid_users || 0}</span>
                        <span className="metric-label">Paid Users</span>
                      </div>
                    </div>
                  </div>

                  <div className="insight-card">
                    <h3>Growth Potential</h3>
                    <div className="insight-content">
                      <div className="insight-metric">
                        <span className="metric-value">{subscriptionAnalytics.metrics?.free_users || 0}</span>
                        <span className="metric-label">Free Users</span>
                      </div>
                      <div className="insight-metric">
                        <span className="metric-value">
                          {formatCurrency((subscriptionAnalytics.metrics?.free_users || 0) * 9.99)}
                        </span>
                        <span className="metric-label">Potential Revenue</span>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default Analytics;